import {ChangeEvent, FC, useEffect} from 'react';
import {useRecoilState, useRecoilStateLoadable, useRecoilValue} from 'recoil';

import {TableToolbar} from './TableToolbar';
import {useDebouncePageable} from '../../../hooks/useDebouncePageable';

import {
  serviceRequestPageable,
  serviceRequestsSortQueryTrigger,
  serviceRequestsState,
} from '../../../store/service-requests/atoms';
import {serviceRequestsQuery} from '../../../store/service-requests/selectors';

import {GridRowParams, GridSortModel} from '@mui/x-data-grid';
import {useNavigate} from 'react-router-dom';
import {useEventBus} from '../../../hooks/useEventBus';
import {IPageableTable} from '../../../shared/types/pageable.types';
import {useRedirectedContractsStore} from '../../../store/redirectedContracts';
import {Table} from '../../Table';
import {getColumns} from './helpers';
import {SelectChangeEvent} from '@mui/material';
import {LoadableHelpers} from '../../../shared/helpers/loadableHelpers';

const ServiceRequestsTable: FC = () => {
  const store = useRedirectedContractsStore();
  const navigate = useNavigate();
  const [loadable, setServiceRequests] = useRecoilStateLoadable(serviceRequestsQuery);
  const {content: serviceRequests, totalPages, totalElements} = useRecoilValue(serviceRequestsState);
  const [sort, setSort] = useRecoilState(serviceRequestsSortQueryTrigger);
  const [pageable, setPageable] = useRecoilState(serviceRequestPageable);
  const {
    pageableToDebounce: {page, size},
    handleChangePage,
    handleChangeRowsPerPage,
    resetPageable,
  } = useDebouncePageable<IPageableTable>(pageable, setPageable);
  const bus = useEventBus();

  const handleRowClick = ({row}: GridRowParams) => {
    store.removeContract(row.id);
    navigate(`/serviceRequests/${row.id}`);
  };

  const handleSortModelChange = (model: GridSortModel) => {
    setSort([...model]);
  };

  const handlePageSizeChange = (e: SelectChangeEvent<number>) => {
    localStorage.setItem('serviceRequestsPerPage', e?.target.value as string);
    handleChangeRowsPerPage(e as ChangeEvent<HTMLInputElement>);
  };

  useEffect(() => {
    LoadableHelpers.handle(loadable, {
      success: setServiceRequests,
      error: bus?.emit,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadable.state]);

  const columns = getColumns();

  return (
    <>
      <TableToolbar resetPageable={resetPageable} loadingState={loadable.state} />
      <Table
        name="serviceRequests"
        loading={LoadableHelpers.isLoading(loadable)}
        rows={serviceRequests}
        columns={columns}
        sortModel={sort}
        pageSize={size}
        totalPages={totalPages}
        currentPage={page}
        startPageFromZero
        totalCount={totalElements}
        onRowClick={handleRowClick}
        onSortModelChange={handleSortModelChange}
        onPageSizeChange={handlePageSizeChange}
        onPaginationChange={handleChangePage}
      />
    </>
  );
};

export default ServiceRequestsTable;
